import { FormattedMessage, useIntl } from 'react-intl';
import { Accordion, Icon, Button } from 'semantic-ui-react';
import { Col, Row, Table } from 'react-bootstrap';
import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { patientInfoSliceSelector } from '../patientInfoSlice';
import { toast } from 'react-toastify';
import { getPatientPayments, getPaymentInvoice } from '../../../utils/apiCalls';
import { ReactComponent as DownloadIcon } from '../../../images/download.svg';
import type { DoctorPatientRelation } from '../../../types/ninox.types';
import { CreatePaymentModal } from './createPaymentModal';

type PatientPaymentProps = {
  activeIndex: number;
  handleClick: (arg0: any, arg1: any) => void;
  patientDoctorsList: DoctorPatientRelation[];
};

interface Payment {
  id: number;
  fields: PaymentFields;
}

interface PaymentFields {
  price: number;
  billedAmount: number;
  discountAmount: number;
  platformFee: number;
  doctorAmount: number;
  currency: string;
  icepayCheckoutId: string;
  paymentStatus: string;
  patientId: number;
  doctorId: number | null;
  productCode: string;
  couponCode: string;
  appointmentId: number | undefined;
  paymentStartedAt: Date;
  paymentCompletedAt: Date;
  fowardDone: boolean;
  forwardDate: Date;
  forwardAttempts: number;
  invoiceNumber: string;
}

export default function PatientPayments({ activeIndex, handleClick, patientDoctorsList }: PatientPaymentProps) {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [paymentsError, setPaymentsError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const intl = useIntl();
  const { patientInfoUpdate } = useSelector(patientInfoSliceSelector);

  const fetchPayments = useCallback(async () => {
    if (!patientInfoUpdate.id) return;
    try {
      const response = await getPatientPayments(patientInfoUpdate.id);
      if (!response.data) {
        setPayments([]);
        return;
      }
      setPaymentsError(null);
      setPayments(response.data);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An error occurred fetching payments.';
      setPaymentsError(message);
      console.error('Error fetching patient payments:', error);
    }
  }, [patientInfoUpdate.id]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleDownload = async (paymentId: number, invoiceNumber: string, productCode: string) => {
    try {
      const response = await getPaymentInvoice(paymentId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const isReservation = productCode.includes('reservation');
      const filenameParts = [];
      if (patientInfoUpdate.firstname) filenameParts.push(patientInfoUpdate.firstname);
      if (patientInfoUpdate.lastname) filenameParts.push(patientInfoUpdate.lastname);
      filenameParts.push(isReservation ? 'zahlungsanforderung.pdf' : `rechnung-${invoiceNumber}.pdf`);
      a.download = filenameParts.join(' ');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(intl.formatMessage({ id: 'invoiceDownloaded' }));
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(intl.formatMessage({ id: 'invoiceDownloadedError' }));
    }
  };

  const formatDate = (date: Date): string => {
    return `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1)
      .toString()
      .padStart(2, '0')}-${date.getFullYear()}`;
  };

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCreatePaymentClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    handleOpenModal();
  };

  return (
    <>
      <Accordion.Title active={activeIndex === 14} index={14} onClick={(event, data) => handleClick(event, data)}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <span>
            <Icon name='dropdown' />
            <FormattedMessage id='patient.payments' />
          </span>
          <Button
            primary
            size='small'
            onClick={handleCreatePaymentClick}
            style={{ visibility: activeIndex === 14 ? 'visible' : 'hidden' }}
          >
            <FormattedMessage id='patient.createPayment' defaultMessage='Create Payment' />
          </Button>
        </div>
      </Accordion.Title>
      <Accordion.Content active={activeIndex === 14}>
        <Row>
          <Col>
            <div>
              {paymentsError && <div>{paymentsError}</div>}
              {!paymentsError && payments?.length === 0 && (
                <FormattedMessage id='paymentsNotFound' defaultMessage='No payments found' />
              )}
              {payments?.length > 0 && (
                <Table>
                  <thead>
                    <tr>
                      <th>
                        <FormattedMessage id='patient.paymentId' />
                      </th>
                      <th>
                        <FormattedMessage id='patient.appointmentId' />
                      </th>
                      <th>
                        <FormattedMessage id='patient.paymentStatus' />
                      </th>
                      <th>
                        <FormattedMessage id='patient.paymentStartedAt' />
                      </th>
                      <th>
                        <FormattedMessage id='patient.price' />
                      </th>
                      <th style={{ width: '320px' }}>
                        <FormattedMessage id='patient.productCode' />
                      </th>
                      <th>
                        <FormattedMessage id='patient.discountCode' defaultMessage='Discount code' />
                      </th>
                      <th>
                        <FormattedMessage id='patient.invoiceNumber' />
                      </th>
                      <th>
                        <FormattedMessage id='patient.invoice' />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment: Payment) => (
                      <tr key={payment.id}>
                        <td>{payment.id}</td>
                        <td>{payment.fields.appointmentId}</td>
                        <td>{payment.fields.paymentStatus}</td>
                        <td>{formatDate(new Date(payment.fields.paymentStartedAt))}</td>
                        <td>{payment.fields.price}</td>
                        <td style={{ maxWidth: '320px', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                          {payment.fields.productCode}
                        </td>
                        <td>{payment.fields.couponCode || '-'}</td>
                        <td>{payment.fields.invoiceNumber || '-'}</td>
                        <td>
                          {payment.fields.invoiceNumber ? (
                            <Button
                              onClick={() =>
                                handleDownload(payment.id, payment.fields.invoiceNumber, payment.fields.productCode)
                              }
                            >
                              <DownloadIcon />
                            </Button>
                          ) : (
                            <p>-</p>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </Col>
        </Row>
      </Accordion.Content>

      {patientInfoUpdate.id && (
        <CreatePaymentModal
          show={isModalOpen}
          setShow={setIsModalOpen}
          patientId={patientInfoUpdate.id}
          patientDoctorsList={patientDoctorsList}
          onSuccess={fetchPayments}
        />
      )}
    </>
  );
}
